import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Button, Modal, Tabs, message} from 'antd';
import {DownloadOutlined} from '@ant-design/icons';
import {useMutation} from '@apollo/client';
import {useTranslation} from 'react-i18next';
import {useLocation, useNavigate} from 'react-router-dom';
import TablePaginationBar from '../../../components/TablePaginationBar';
import ParcelsDownloadModal from '../../central/parcels/components/ParcelsDownloadModal';
import {useDownloadCooldown} from '../../../hooks/useDownloadCooldown';
import {useWarehouseContext} from '../../../contexts/WarehouseContext';
import {getUserInfo} from '../../../utils/auth';
import {extractGraphQLMessageKey} from '../../../utils/graphqlUtils';
import {useAllDspsForWmsPermission} from '../../delivery/accounts/graphql/queries/getAllDspsForWmsPermission';
import PenaltyAppealDrawer from './components/PenaltyAppealDrawer';
import PenaltyCenterFilter from './components/PenaltyCenterFilter';
import PenaltyCenterTable from './components/PenaltyCenterTable';
import {usePenaltyCenterList, usePenaltyCenterTabCounts} from './hooks/usePenaltyCenterQuery';
import {
    APPEAL_PARTNER_PENALTY,
    AppealPartnerPenaltyResponse,
    AppealPartnerPenaltyVariables,
    GENERATE_PARTNER_PENALTY_REPORT,
    GENERATE_WAREHOUSE_PENALTY_REPORT,
    GeneratePartnerPenaltyReportResponse,
    GeneratePartnerPenaltyReportVariables,
} from './graphql/mutations';
import {
    PartnerPenaltyItem,
    PartnerPenaltyStatus,
    PenaltyCenterMode,
    PENALTY_APPEAL_ACTION,
    STALE_PENALTY_ERROR_KEYS,
    PENALTY_CENTER_TABS,
} from './graphql/types';
import {
    buildPenaltyFiltersInput,
    buildWarehousePenaltyFiltersInput,
    getDefaultPenaltyCenterFilters,
    PenaltyCenterFilters,
} from './utils/penaltyCenterUtils';

const DEFAULT_PAGE_SIZE = 20;

interface PenaltyCenterPageProps {
    /** 'warehouse' 为仓库侧只读视图（多 DSP 维度、无申诉操作）；默认 DSP 侧 */
    mode?: PenaltyCenterMode;
}

/**
 * 罚单管理中心。
 * DSP 侧：任何请求都不带 dspId，数据隔离由后端从登录上下文取（见 DspQuery.getDspForDspManager）。
 * 仓库侧：显式传当前仓 warehouseId，后端按资源码 + 仓库权限双重校验，只读、不提供申诉入口。
 */
const PenaltyCenterPage: React.FC<PenaltyCenterPageProps> = ({mode = 'dsp'}) => {
    const {t, i18n} = useTranslation();
    // 本页在 /partner-control/penaltyCenter 与 /delivery/penalty-center 下各挂载一次，
    // 详情用相对当前路径跳转，避免写死其中一个前缀
    const navigate = useNavigate();
    const location = useLocation();
    const {currentWarehouse} = useWarehouseContext();
    const isWarehouse = mode === 'warehouse';
    const warehouseId = Number(currentWarehouse?.id ?? 0);
    const [activeTab, setActiveTab] = useState<PartnerPenaltyStatus>(PENALTY_CENTER_TABS[0]);
    const [filters, setFilters] = useState<PenaltyCenterFilters>(getDefaultPenaltyCenterFilters);
    const [tempFilters, setTempFilters] = useState<PenaltyCenterFilters>(filters);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [appealPenaltyId, setAppealPenaltyId] = useState<number | null>(null);
    const [downloadModalVisible, setDownloadModalVisible] = useState(false);
    const [generatedFileName, setGeneratedFileName] = useState('');

    const {downloadLoading, setDownloadLoading, cooldownRemaining, getLastDownloadTime, setLastDownloadTime} =
        useDownloadCooldown(`partnerPenaltyDownloadLastTime_${getUserInfo()?.account?.id}`);
    const [appealPartnerPenalty] = useMutation<AppealPartnerPenaltyResponse, AppealPartnerPenaltyVariables>(
        APPEAL_PARTNER_PENALTY
    );
    const [generatePartnerPenaltyReport] = useMutation<
        GeneratePartnerPenaltyReportResponse,
        GeneratePartnerPenaltyReportVariables
    >(isWarehouse ? GENERATE_WAREHOUSE_PENALTY_REPORT : GENERATE_PARTNER_PENALTY_REPORT);

    /** 两种模式唯一的入参差异：warehouse 侧多带 warehouseId / dspId */
    const buildFiltersInput = useCallback(
        (sourceFilters: PenaltyCenterFilters) =>
            isWarehouse
                ? buildWarehousePenaltyFiltersInput(sourceFilters, warehouseId)
                : buildPenaltyFiltersInput(sourceFilters),
        [isWarehouse, warehouseId]
    );

    // 仓库未选定时一律不发请求，否则会打出 warehouseId=0 的无效查询
    const warehouseReady = !isWarehouse || warehouseId > 0;

    const sharedFiltersInput = useMemo(() => buildFiltersInput(filters), [buildFiltersInput, filters]);

    const {allDspsForWmsPermission, loading: dspLoading} = useAllDspsForWmsPermission(
        currentWarehouse?.id ?? null,
        !isWarehouse
    );
    const dspOptions = useMemo(
        () =>
            allDspsForWmsPermission
                .filter((dsp) => !!dsp?.id)
                .map((dsp) => ({value: Number(dsp.id), label: dsp.name})),
        [allDspsForWmsPermission]
    );

    const {data, loading, totalElements, executeQuery} = usePenaltyCenterList(mode);
    const {getCount, refetchCounts} = usePenaltyCenterTabCounts(sharedFiltersInput, mode, !warehouseReady);

    /**
     * 列表查询的全套条件必须每次显式传入，否则 setState 异步会用到旧值
     * （切 Tab / 搜索 / 翻页都发生在同一轮渲染里）。
     */
    const runListQuery = useCallback(
        (
            sourceFilters: PenaltyCenterFilters,
            status: PartnerPenaltyStatus,
            targetPage: number,
            targetSize: number
        ) => {
            if (!warehouseReady) return;
            executeQuery(buildFiltersInput(sourceFilters), status, targetPage, targetSize);
        },
        [executeQuery, buildFiltersInput, warehouseReady]
    );

    // 首屏查一次；warehouse 模式下切仓等同于换了数据范围，回到第一页重查
    useEffect(() => {
        setPage(1);
        runListQuery(filters, activeTab, 1, pageSize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [warehouseId]);

    const handleTabChange = (key: string) => {
        const nextTab = key as PartnerPenaltyStatus;
        setActiveTab(nextTab);
        setPage(1);
        runListQuery(filters, nextTab, 1, pageSize);
        void refetchCounts();
    };

    const handleSearch = () => {
        setFilters(tempFilters);
        setPage(1);
        runListQuery(tempFilters, activeTab, 1, pageSize);
    };

    const handleReset = () => {
        const defaults = getDefaultPenaltyCenterFilters();
        setTempFilters(defaults);
        setFilters(defaults);
        setPage(1);
        runListQuery(defaults, activeTab, 1, pageSize);
    };

    const handlePageChange = (nextPage: number, nextSize: number) => {
        if (nextSize !== pageSize) {
            setPage(1);
            setPageSize(nextSize);
            runListQuery(filters, activeTab, 1, nextSize);
        } else {
            setPage(nextPage);
            runListQuery(filters, activeTab, nextPage, pageSize);
        }
    };

    /** 申诉成功后该行流转到「审核中」，离开当前 Tab，列表与 Tab 计数都要刷 */
    const refreshAfterStatusChange = () => {
        runListQuery(filters, activeTab, page, pageSize);
        void refetchCounts();
    };

    /**
     * 放弃申诉没有材料要填，保留二次确认弹窗（发起申诉走抽屉表单）。
     * 放弃后流转到「申诉认罚」——该状态无对应 Tab，直接从列表消失。
     */
    const runAbandon = (record: PartnerPenaltyItem) => {
        Modal.confirm({
            title: t('partnerControl.penaltyCenter.confirm.abandonTitle'),
            content: t('partnerControl.penaltyCenter.confirm.abandonContent', {
                penaltyNo: record.penaltyNo,
            }),
            okText: t('partnerControl.penaltyCenter.confirm.ok'),
            cancelText: t('partnerControl.penaltyCenter.confirm.cancel'),
            onOk: async () => {
                try {
                    await appealPartnerPenalty({
                        variables: {id: record.id, action: PENALTY_APPEAL_ACTION.ABANDON},
                    });
                    message.success(t('partnerControl.penaltyCenter.message.abandonSuccess'));
                    refreshAfterStatusChange();
                } catch (e) {
                    const messageKey = extractGraphQLMessageKey(e);
                    if (messageKey && STALE_PENALTY_ERROR_KEYS.includes(messageKey)) {
                        message.error(t(messageKey));
                        refreshAfterStatusChange();
                        return;
                    }
                    message.error(t('partnerControl.penaltyCenter.message.failed'));
                }
            },
        });
    };

    /** 导出当前筛选 + 当前 Tab 的全量（异步生成，去下载中心领取） */
    const handleExport = useCallback(async () => {
        const currentTime = Date.now();
        if (currentTime - getLastDownloadTime() < 5000 || downloadLoading) {
            message.warning(t('partnerControl.penaltyCenter.export.generating'));
            return;
        }
        try {
            setDownloadLoading(true);
            setLastDownloadTime(currentTime);
            const language = i18n.language.split('-')[0];
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const response = await generatePartnerPenaltyReport({
                variables: {
                    filters: {...buildFiltersInput(filters), statuses: [activeTab]},
                    language,
                    timezone,
                },
            });
            const result = response.data?.generatePartnerPenaltyReport;
            if (result?.success && result.fileName) {
                setGeneratedFileName(result.fileName);
                setDownloadModalVisible(true);
            } else if (result?.errorCode?.startsWith('EXCEED_MAX_LIMIT')) {
                const max = result.errorCode.split(':')[1] ?? '';
                message.error(t('partnerControl.penaltyCenter.export.exceedMaxLimit', {max}));
            } else if (result?.errorCode === 'NO_DATA') {
                message.warning(t('partnerControl.penaltyCenter.export.noData'));
            } else {
                message.error(t('partnerControl.penaltyCenter.export.failed'));
            }
        } catch (e) {
            message.error(t('partnerControl.penaltyCenter.export.failed'));
            setDownloadLoading(false);
        }
    }, [
        downloadLoading,
        getLastDownloadTime,
        setDownloadLoading,
        setLastDownloadTime,
        filters,
        activeTab,
        i18n.language,
        generatePartnerPenaltyReport,
        buildFiltersInput,
        t,
    ]);

    const tabItems = PENALTY_CENTER_TABS.map((status) => ({
        key: status,
        label: `${t(`partnerControl.penaltyCenter.status.${status}`)} (${getCount(status)})`,
    }));

    return (
        <div>
            <h1 className="text-2xl font-bold mb-4">
                {t('partnerControl.penaltyCenter.title')}
            </h1>

            {/* 筛选与列表拆成两张卡片：筛选在上，Tab 归入列表卡片 */}
            <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
                <PenaltyCenterFilter
                    filters={tempFilters}
                    onChange={setTempFilters}
                    onSearch={handleSearch}
                    onReset={handleReset}
                    mode={mode}
                    dspOptions={dspOptions}
                    dspLoading={dspLoading}
                    extraActions={
                        <Button
                            icon={<DownloadOutlined />}
                            onClick={() => void handleExport()}
                            loading={cooldownRemaining > 0}
                            disabled={downloadLoading || cooldownRemaining > 0}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">
                                {cooldownRemaining > 0
                                    ? `${t('partnerControl.penaltyCenter.export.button')} ${cooldownRemaining}s`
                                    : t('partnerControl.penaltyCenter.export.button')}
                            </span>
                        </Button>
                    }
                />
            </div>

            <div className="bg-white rounded-lg shadow-sm px-4 pb-3">
                <Tabs activeKey={activeTab} items={tabItems} onChange={handleTabChange} />
                <PenaltyCenterTable
                    data={data}
                    loading={loading}
                    mode={mode}
                    // 仓库侧只读：不出操作列（后端也拒绝仓库账号的写操作，不只是前端藏按钮）
                    showAction={!isWarehouse && activeTab === PartnerPenaltyStatus.PENDING_SUBMIT}
                    onAppeal={(record) => setAppealPenaltyId(record.id)}
                    onAbandon={runAbandon}
                    onViewDetail={(record) => navigate(`${location.pathname}/${record.id}`)}
                />
                <TablePaginationBar
                    total={totalElements}
                    current={page}
                    pageSize={pageSize}
                    onChange={handlePageChange}
                />
            </div>

            {!isWarehouse && (
                <PenaltyAppealDrawer
                    open={appealPenaltyId !== null}
                    penaltyId={appealPenaltyId}
                    onClose={() => setAppealPenaltyId(null)}
                    onSubmitted={refreshAfterStatusChange}
                />
            )}

            <ParcelsDownloadModal
                visible={downloadModalVisible}
                onClose={() => setDownloadModalVisible(false)}
                fileName={generatedFileName}
            />
        </div>
    );
};

export default PenaltyCenterPage;
